import {useSearchParams} from 'react-router-dom';
import {useQuery} from '@tanstack/react-query';
import {Spinner} from '@kroll-resolver-crisp/ui-components-react';
import {
  useJobs,
  useFolders,
  useClassificationConfig,
  useLanguages,
  useTopics,
  useLocations
} from '../api/hooks';
import {InvestigationsWorkspace} from '../components/investigations/InvestigationsWorkspace';
import {JOBS_PAGE_SIZE, jobsApi} from '../api/jobs';

export function InvestigationsPage() {
  const [searchParameters] = useSearchParams();
  const selectedId = searchParameters.get('id');
  // Only the unfiled list loads up front: each folder fetches its own jobs when it is opened.
  const jobs = useJobs({unfiled: true, page: 1, page_size: JOBS_PAGE_SIZE});
  // A deep-linked job may sit in a folder whose jobs aren't loaded yet, so it is fetched directly.
  const linkedJob = useQuery({
    queryKey: ['jobs', 'linked', selectedId],
    queryFn: async () => jobsApi.get(selectedId!),
    enabled: Boolean(selectedId),
    retry: false
  });
  const folders = useFolders();
  const classifications = useClassificationConfig();
  const languages = useLanguages();
  const topics = useTopics();
  const locations = useLocations();

  // Gate on the queries whose data seeds workspace local state (jobs/folders).
  if (jobs.isLoading || folders.isLoading || linkedJob.isLoading) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <Spinner className="size-6" aria-label="Loading investigations" />
      </div>
    );
  }

  const unfiled = jobs.data?.results ?? [];
  const initialJobs =
    linkedJob.data && !unfiled.some((j) => j.id === linkedJob.data.id)
      ? [linkedJob.data, ...unfiled]
      : unfiled;

  return (
    <InvestigationsWorkspace
      jobs={initialJobs}
      jobsHasMore={jobs.data?.has_more ?? false}
      folders={folders.data ?? []}
      {...(selectedId ? {selectedId} : {})}
      classifications={classifications.data ?? []}
      languages={languages.data ?? []}
      topics={topics.data ?? []}
      locations={locations.data ?? []}
    />
  );
}
